import { createFileRoute, Link } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Plus, Copy, Download } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { adminApi, formatPromoGrant, formatPromoRedeemed, type CreatePromoCodesInput, type PromoCodeRow } from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/DataTable";
import { StatusPill } from "@/components/StatusPill";
import { StateFilterSelect } from "@/components/StateFilterSelect";
import { DetailDrawer } from "@/components/DetailDrawer";
import { downloadCsv } from "@/lib/csv";
import { queryTableProps } from "@/lib/data-table";
import { formatLondon, isoUtc } from "@/lib/format-time";
import { promoCodeTone } from "@/lib/status-tone";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authed/promo-codes")({
  component: PromoCodesPage,
});

const STATUS_OPTIONS = ["active", "disabled"] as const;
const PAGE_SIZE = 50;

type CodeMode = "generated" | "custom";
type GrantMode = "days" | "until";

interface FormState {
  codeMode: CodeMode;
  count: string;
  prefix: string;
  customCode: string;
  grantMode: GrantMode;
  grantDays: string;
  /** yyyy-mm-dd; sent as 23:59:59 UTC of that day. */
  grantUntilDate: string;
  maxRedemptions: string;
  /** yyyy-mm-dd; sent as 23:59:59 UTC of that day, omitted when blank. */
  expiresAtDate: string;
  requiresSchoolUrn: string;
  campaign: string;
  note: string;
}

const EMPTY_FORM: FormState = {
  codeMode: "generated",
  count: "1",
  prefix: "",
  customCode: "",
  grantMode: "days",
  grantDays: "30",
  grantUntilDate: "",
  maxRedemptions: "",
  expiresAtDate: "",
  requiresSchoolUrn: "",
  campaign: "",
  note: "",
};

/** 23:59:59 UTC of the given yyyy-mm-dd date, as an ISO string. */
function endOfDayUtcIso(dateStr: string): string {
  return `${dateStr}T23:59:59.000Z`;
}

/** Returns the callable input once every required field is valid, else null (drives the submit button's disabled state). */
function buildInput(form: FormState): CreatePromoCodesInput | null {
  const campaign = form.campaign.trim();
  if (!campaign) return null;

  const input: CreatePromoCodesInput = { campaign };

  if (form.codeMode === "custom") {
    const customCode = form.customCode.trim();
    if (!customCode) return null;
    input.customCode = customCode;
  } else {
    const count = parseInt(form.count, 10);
    if (!Number.isFinite(count) || count < 1 || count > 500) return null;
    input.count = count;
    if (form.prefix.trim()) input.prefix = form.prefix.trim().toUpperCase();
  }

  if (form.grantMode === "days") {
    const days = parseInt(form.grantDays, 10);
    if (!Number.isFinite(days) || days <= 0) return null;
    input.grantDays = days;
  } else {
    if (!form.grantUntilDate) return null;
    input.grantUntil = endOfDayUtcIso(form.grantUntilDate);
  }

  if (form.maxRedemptions.trim()) {
    const max = parseInt(form.maxRedemptions, 10);
    if (!Number.isFinite(max) || max <= 0) return null;
    input.maxRedemptions = max;
  }

  if (form.expiresAtDate) input.expiresAt = endOfDayUtcIso(form.expiresAtDate);
  if (form.requiresSchoolUrn.trim()) input.requiresSchoolUrn = form.requiresSchoolUrn.trim();
  if (form.note.trim()) input.note = form.note.trim();

  return input;
}

function PromoCodesPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<"" | "active" | "disabled">("");
  const [campaign, setCampaign] = useState("");
  const debouncedCampaign = useDebouncedValue(campaign);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [createdCodes, setCreatedCodes] = useState<{ codes: string[]; campaign: string } | null>(
    null,
  );
  const [copied, setCopied] = useState(false);

  const filters = useMemo(
    () => ({
      status: status || undefined,
      campaign: debouncedCampaign.trim() || undefined,
      limit: PAGE_SIZE,
    }),
    [status, debouncedCampaign],
  );

  const query = useInfiniteQuery({
    queryKey: queryKeys.promoCodes.list(filters),
    queryFn: async ({ pageParam }) => {
      const res = await adminApi.listPromoCodes()({ ...filters, cursor: pageParam ?? null });
      return res.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });

  const rows = query.data?.pages.flatMap((p) => p.items) ?? [];

  const create = useMutation({
    mutationFn: async (input: CreatePromoCodesInput) =>
      (await adminApi.createPromoCodes()(input)).data,
    onSuccess: (data, input) => {
      setCreatedCodes({ codes: data.codes, campaign: input.campaign });
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setCopied(false);
      void qc.invalidateQueries({ queryKey: queryKeys.promoCodes.all });
    },
  });

  const input = buildInput(form);

  function handleCopyAll() {
    if (!createdCodes) return;
    void navigator.clipboard.writeText(createdCodes.codes.join("\n"));
    setCopied(true);
  }

  const columns = useMemo<ColumnDef<PromoCodeRow, unknown>[]>(
    () => [
      {
        header: "Code",
        accessorKey: "code",
        cell: ({ row }) => (
          <Link
            to="/promo-codes/$code"
            params={{ code: row.original.code }}
            className="font-mono text-xs text-primary hover:underline"
          >
            {row.original.code}
          </Link>
        ),
      },
      { header: "Campaign", accessorKey: "campaign" },
      {
        header: "Grant",
        cell: ({ row }) => formatPromoGrant(row.original, formatLondon),
      },
      {
        header: "Redeemed",
        cell: ({ row }) => formatPromoRedeemed(row.original),
      },
      {
        header: "Expires",
        cell: ({ row }) =>
          row.original.expiresAt ? (
            <span title={isoUtc(row.original.expiresAt)}>
              {formatLondon(row.original.expiresAt)}
            </span>
          ) : (
            "—"
          ),
      },
      {
        header: "Status",
        cell: ({ row }) => (
          <StatusPill tone={promoCodeTone(row.original.status)}>{row.original.status}</StatusPill>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Promo codes</h1>
          <p className="text-sm text-muted-foreground">
            Codes that grant a fixed-term premium entitlement on redemption. Creation and status
            changes are audit-logged.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="mr-1 h-4 w-4" /> Create codes
        </Button>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <Field label="Status">
            <StateFilterSelect
              value={status}
              onChange={setStatus}
              options={STATUS_OPTIONS}
              allLabel="All statuses"
            />
          </Field>
          <div className="min-w-[220px]">
            <Field label="Campaign">
              <Input
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
                placeholder="e.g. schools-autumn-2026"
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      {create.isError ? (
        <div className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {(create.error as Error).message}
        </div>
      ) : null}

      {createdCodes ? (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="text-sm">
              Created <strong>{createdCodes.codes.length}</strong> code(s) for campaign{" "}
              <code>{createdCodes.campaign}</code>.
            </div>
            <div className="max-h-40 overflow-y-auto rounded border bg-muted p-2 font-mono text-xs">
              {createdCodes.codes.join("\n")}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() =>
                  downloadCsv(
                    `promo-codes-${createdCodes.campaign}.csv`,
                    createdCodes.codes.map((code) => ({ code, campaign: createdCodes.campaign })),
                    ["code", "campaign"],
                  )
                }
              >
                <Download className="mr-1 h-3 w-3" /> Download CSV
              </Button>
              <Button size="sm" variant="outline" onClick={handleCopyAll}>
                <Copy className="mr-1 h-3 w-3" /> {copied ? "Copied" : "Copy all"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setCreatedCodes(null)}>
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <DataTable
        data={rows}
        columns={columns}
        rowKey={(row) => row.code}
        emptyMessage="No promo codes match this filter."
        {...queryTableProps(query)}
        hasMore={!!query.hasNextPage}
        isFetchingMore={query.isFetchingNextPage}
        onLoadMore={() => void query.fetchNextPage()}
      />

      {showCreate ? (
        <DetailDrawer
          title="Create promo codes"
          onClose={() => setShowCreate(false)}
          footer={
            <Button
              className="w-full"
              disabled={!input || create.isPending}
              onClick={() => input && create.mutate(input)}
            >
              {create.isPending ? "Creating…" : "Create"}
            </Button>
          }
        >
          <CreatePromoCodeForm form={form} setForm={setForm} />
        </DetailDrawer>
      ) : null}
    </div>
  );
}

function ModeToggle<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="inline-flex rounded-md border p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded px-3 py-1 text-xs font-medium transition-colors",
            value === o.value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function CreatePromoCodeForm({
  form,
  setForm,
}: {
  form: FormState;
  setForm: (form: FormState) => void;
}) {
  function patch(p: Partial<FormState>) {
    setForm({ ...form, ...p });
  }

  return (
    <div className="space-y-4">
      <Field label="Code type">
        <ModeToggle
          value={form.codeMode}
          onChange={(v) => patch({ codeMode: v })}
          options={[
            { value: "generated", label: "Generated" },
            { value: "custom", label: "Custom" },
          ]}
        />
      </Field>

      {form.codeMode === "generated" ? (
        <div className="flex flex-wrap gap-3">
          <Field label="Count (1-500)">
            <Input
              type="number"
              min={1}
              max={500}
              value={form.count}
              onChange={(e) => patch({ count: e.target.value })}
              className="w-28"
            />
          </Field>
          <Field label="Prefix (optional)">
            <Input
              value={form.prefix}
              onChange={(e) => patch({ prefix: e.target.value.toUpperCase() })}
              maxLength={12}
              placeholder="e.g. SCHOOL"
              className="w-32"
            />
          </Field>
        </div>
      ) : (
        <Field label="Custom code">
          <Input
            value={form.customCode}
            onChange={(e) => patch({ customCode: e.target.value.toUpperCase() })}
            placeholder="e.g. WELCOME2026"
          />
        </Field>
      )}

      <Field label="Grant">
        <ModeToggle
          value={form.grantMode}
          onChange={(v) => patch({ grantMode: v })}
          options={[
            { value: "days", label: "Days" },
            { value: "until", label: "Until date" },
          ]}
        />
      </Field>

      {form.grantMode === "days" ? (
        <Field label="Grant days">
          <Input
            type="number"
            min={1}
            value={form.grantDays}
            onChange={(e) => patch({ grantDays: e.target.value })}
            className="w-28"
          />
        </Field>
      ) : (
        <Field label="Grant until (UTC end of day)">
          <Input
            type="date"
            value={form.grantUntilDate}
            onChange={(e) => patch({ grantUntilDate: e.target.value })}
            className="w-44"
          />
        </Field>
      )}

      <Field label="Max redemptions (blank = unlimited)">
        <Input
          type="number"
          min={1}
          value={form.maxRedemptions}
          onChange={(e) => patch({ maxRedemptions: e.target.value })}
          className="w-32"
        />
      </Field>

      <Field label="Code expiry date (blank = none)">
        <Input
          type="date"
          value={form.expiresAtDate}
          onChange={(e) => patch({ expiresAtDate: e.target.value })}
          className="w-44"
        />
      </Field>

      <Field label="Requires school URN (optional)">
        <Input
          value={form.requiresSchoolUrn}
          onChange={(e) => patch({ requiresSchoolUrn: e.target.value })}
          placeholder="e.g. 100001"
        />
      </Field>

      <Field label="Campaign (required)">
        <Input
          value={form.campaign}
          onChange={(e) => patch({ campaign: e.target.value })}
          placeholder="e.g. schools-autumn-2026"
        />
      </Field>

      <Field label="Note (optional)">
        <Input value={form.note} onChange={(e) => patch({ note: e.target.value })} maxLength={500} />
      </Field>
    </div>
  );
}
